import { memo, useCallback, useRef, useState } from "react"
import { PayoffLeg, PayoffProfitData, SdData } from "../payoffContainer/livePayoffContainer"
import { optionsTradesDataType } from "../../pages/typings/options.typings"
import { useStyles } from "./payoffGraphStyles"
import { Box } from "@mui/material"
import PayoffGraphFooter from "./payoffGraphFooter"
import PayoffChartWrapper from "./payoffChartWrapper"
import { PayoffStrikeData } from "../../reducer/strategyBuilder.reducer"
import { PayoffGraphContext, PayoffGraphContextType } from "./payoffGraphContext"
import { OrderSlidersElements } from "./payoffGraphOrderTags/payoffGraphOrderTags"
import { StrikeClickInfo } from "./payoffChart/payoffChart.typings"
import AddLegMenu from "./addLegMenu"
import { TransactionType } from "../../proto/generated/order"
import { Security } from "../../proto/generated/security"
import { selectIsEmbedded } from "../../selectors/device"
import { useSelector } from "react-redux"
import { selectUserPreferences } from "../../selectors/user"

export type PayoffGraphProps = {
    sdData: SdData[],
    payoffLegs: PayoffLeg[]
    profitData: PayoffProfitData[],
    breakevenPoints: number[],
    payoffStrikes: PayoffStrikeData[],
    spotPrice: number,
    optionsTradesData: optionsTradesDataType,
    addLegEnabled: boolean,
    readonly?: boolean,
    updateLegStrike: (legId: string, strike: number) => void,
    onAddLeg: (transactionType: TransactionType, security: Security) => void,
    onAddLegClick: (string: number) => void
}

const PayoffGraph: React.FC<PayoffGraphProps> = ({
    readonly, sdData, payoffLegs, breakevenPoints, optionsTradesData, profitData, spotPrice,
    payoffStrikes, updateLegStrike, onAddLeg, onAddLegClick, addLegEnabled }) => {
        const isEmbedded = useSelector(selectIsEmbedded);

    const { classes } = useStyles()
    const userPreferences = useSelector(selectUserPreferences)
    const { payOffSettings } = userPreferences
    const { showSdLines = true, showOiBars = true } = payOffSettings ?? {}
    const [sliderElements, setSliderElements] = useState<OrderSlidersElements>(null);
    const [ strikeClickInfo, setStrikeClickInfo ] = useState<StrikeClickInfo>(null)
    const [chartAnchorContainer, setChartAnchorContainer] = useState<HTMLDivElement | null>(null)
    const payoffGraphContext = useRef<PayoffGraphContextType>({
        updateSliderElements(elements) {
            setSliderElements(elements);
        },
        setPayoffChart(chart) {
            payoffGraphContext.current.payoffChart = chart;
        },
        setChartAnchorContainer(container) {
            setChartAnchorContainer(container);
        }
    });

    const onAddLegClickInternal = useCallback((clickInfo: StrikeClickInfo) => {
        onAddLegClick && onAddLegClick(clickInfo.strike)
        setStrikeClickInfo(clickInfo)
    }, [onAddLegClick]);
    
    const onAddLegMenuClose = useCallback(() => setStrikeClickInfo(null), [])

    return  <PayoffGraphContext.Provider value={payoffGraphContext.current}>
                <Box className={`font-feature-num ${classes.container}`}  >
                    <Box className={classes.chart}>
                        {((readonly || sliderElements) && profitData && payoffLegs.length) ? (
                            <PayoffChartWrapper sdData={sdData} payoffLegs={payoffLegs} profitData={profitData} spotPrice={spotPrice}
                                payoffStrikes={payoffStrikes} orderSlidersElements={sliderElements} breakevenPoints={breakevenPoints}
                                showSdLines={showSdLines} showOiBars={showOiBars}
                                updateLegStrike={updateLegStrike} onAddLegClick={onAddLegClickInternal} addLegEnabled={addLegEnabled} showSettingsButton />
                            ) : null}
                    </Box>
                    <Box className={classes.footer}>
                        <PayoffGraphFooter payoffLegs={payoffLegs} readonly={readonly || !addLegEnabled} />
                    </Box>
                </Box>
                {strikeClickInfo && chartAnchorContainer && !isEmbedded && optionsTradesData?.trades?.length ?
                    <AddLegMenu
                        clickInfo={strikeClickInfo}
                        optionsTradesData={optionsTradesData}
                        anchorContainer={chartAnchorContainer}
                        onClose={onAddLegMenuClose}
                        onAddLeg={onAddLeg}
                    /> : null }
            </PayoffGraphContext.Provider>
}


export default memo(PayoffGraph)